import type { QueryReturnValue } from '@reduxjs/toolkit/query';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { clearCombinedToken as clearAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import type {
    Hub,
    Org,
} from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import {
    getCurrentEvoInstance,
    getLicenseToken,
    setAvailableEvoInstances,
    setCentralInstances,
    setLicenseToken,
} from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { EntitlementsApiClient } from '../EntitlementsApiClient';
import type { APICentralInstances, CentralInstance, CentralInstances } from './Entitlements.types';

export const CentralEntitlementsApi = EntitlementsApiClient.injectEndpoints({
    endpoints: (build) => ({
        loadCentralInstances: build.query<CentralInstances, void>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery('v1/licenses/id/central/servers');
                if (result.error) {
                    return { error: result.error };
                }
                const data = result.data as APICentralInstances;
                const instances: CentralInstances = {
                    servers: [],
                };
                data.servers.forEach((apiInstance) => {
                    const convertedInstance: CentralInstance = {
                        displayName: apiInstance.display_name,
                        host: apiInstance.host,
                        port: apiInstance.port,
                        instanceUUID: apiInstance.instance_id,
                        entitlementId: apiInstance.entitlement_id,
                        name: apiInstance.name,
                        organizationName: apiInstance.organization_name,
                        validTo: apiInstance.valid_to,
                    };
                    instances.servers.push(convertedInstance);
                });

                setCentralInstances(instances);
                return { data: instances };
            },
        }),
    }),
});

export type Discovery = {
    hubs: Array<Hub>;
    services: Array<{ code: string; display_name: string }>;
    organizations: Array<Org>;
    service_access: Array<{ hub_code: string; org_id: string; services: string[] }>;
    has_new_instances?: boolean;
};

const EntitlementApiWithTags = EntitlementsApiClient.enhanceEndpoints({ addTagTypes: ['Token'] });

const tokenMutex = new Mutex();

export const EntitlementsApi = EntitlementApiWithTags.injectEndpoints({
    endpoints: (build) => ({
        loadLicenseOrgs: build.query<Discovery, { service: string }>({
            async queryFn({ service }, _queryApi, _extraOptions, baseQuery) {
                // Get the hub and org
                const discoveryResponse = await baseQuery({
                    url: '/evo/identity/v1/discovery',
                    params: { service },
                });
                if (discoveryResponse.error) {
                    // It is possible that if the user refreshes the page fast enough, that the original
                    // request will be cancelled and RTK Query will return the
                    // vague error: { status: 'FETCH_ERROR', error: 'TypeError: Failed to fetch' }
                    // In this case, the access token is not invalid, and we do not want to redirect the user
                    // back to the login page.
                    const { status } = discoveryResponse.error as any;
                    if (status && typeof status === 'number' && status >= 400) {
                        clearAccessToken();
                    }
                    return { error: discoveryResponse.error };
                }
                const discovery = discoveryResponse.data as {
                    discovery: Discovery;
                };
                if (!discovery || !discovery.discovery) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'Error: No licenses' } };
                }

                // Early return if no data.
                if (!discovery.discovery.service_access.length) {
                    setAvailableEvoInstances([]);
                    return { data: discovery.discovery };
                }

                const hubs = new Map<string, Hub>();
                for (const hub of discovery.discovery.hubs) {
                    hubs.set(hub.code, hub);
                }
                const orgs = new Map<string, Org>();
                for (const org of discovery.discovery.organizations) {
                    orgs.set(org.id, org);
                }
                const availableEvoInstances = [];
                for (const sa of discovery.discovery.service_access) {
                    const hub = hubs.get(sa.hub_code);
                    const org = orgs.get(sa.org_id);
                    if (sa.services.includes(service) && hub && org) {
                        availableEvoInstances.push({ org, hub });
                    }
                }
                setAvailableEvoInstances(availableEvoInstances);
                if (availableEvoInstances.length === 0) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: `Error: No valid org/hub for ${service}`,
                        },
                    };
                }
                return { data: discovery.discovery };
            },
        }),
        loadLicenseToken: build.query<string, { service: string }>({
            async queryFn({ service }, _queryApi, _extraOptions, baseQuery) {
                const { org, hub } = getCurrentEvoInstance();
                if (!org || !hub) {
                    return { error: { status: 500, data: 'No instance to get license for' } };
                }
                // Mutex prevents licensed calls while waiting for new token
                await tokenMutex.waitForUnlock();
                const release = await tokenMutex.acquire();
                let resp;
                try {
                    resp = (await baseQuery({
                        url: '/evo/identity/v1/token',
                        params: {
                            org_id: org.id,
                            service,
                            hub: hub.code,
                        },
                        responseHandler: (response) =>
                            response.text().then((s) => {
                                const token = s.trimStart().trimEnd();
                                if (response.status === 200) {
                                    setLicenseToken(token);
                                }
                                return token;
                            }),
                    })) as QueryReturnValue<string, FetchBaseQueryError, undefined>;
                } finally {
                    // Release the mutex
                    release();
                }
                return resp;
            },
            keepUnusedDataFor: 1200, // 20 min
            providesTags: ['Token'],
        }),
    }),
});

export function createLicensedBaseQuery(
    url: () => string | null,
    service: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
    return async (args, WebApi, extraOptions) => {
        const baseUrl = url();
        if (baseUrl === null) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }
        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
                const token = getLicenseToken();
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                }
                return headers;
            },
        });
        // We have a mutex to prevent fetching a token multiple times if a number of calls happen at once.
        await tokenMutex.waitForUnlock();
        let result = await rawBaseQuery(args, WebApi, extraOptions);
        // If the result errors out with a 401, we probably have an expired license token.
        if (result.error && result.error.status === 401) {
            const { org, hub } = getCurrentEvoInstance();
            if (!org || !hub) {
                // Return the error, we don't have enough information to get a new one.
                return result;
            }
            if (!tokenMutex.isLocked()) {
                const originalToken = getLicenseToken();
                // Actually fetch the new license token, forcing a refetch that
                // subscribes to result to make sure the request is completed
                await WebApi.dispatch(
                    EntitlementsApi.endpoints.loadLicenseToken.initiate(
                        { service },
                        { subscribe: true, forceRefetch: true },
                    ),
                );
                // If we actually got a new token, try the original query again.
                const newToken = getLicenseToken();
                if (originalToken !== newToken) {
                    // retry the initial query
                    result = await rawBaseQuery(args, WebApi, extraOptions);
                }
            } else {
                // There is another query fetching a token. Wait for that, and try query again assuming we have a new
                // token now.
                await tokenMutex.waitForUnlock();
                result = await rawBaseQuery(args, WebApi, extraOptions);
            }
        }
        return result;
    };
}

export const { useLoadCentralInstancesQuery } = CentralEntitlementsApi;

export const { useLoadLicenseOrgsQuery, useLoadLicenseTokenQuery } = EntitlementsApi;
